// Blog: August 2026 launch of the Party Central data explorer.
import { buildSupportMailto } from '../../../configs/brandLinks';
import { defineDocument, em, link, list, p, section, text } from '../../schema';
export const blogPartyCentralIsLiveDocument = defineDocument({
    id: 'blog:party-central-is-live',
    kind: 'blog',
    slug: 'party-central-is-live',
    route: '/blog/party-central-is-live',
    seo: {
        title: 'Party Central Is Live',
        description: 'The Twitch party game data behind our monthly reports is now yours to explore.',
        imageAsset: 'blogPartyCentral',
        imageAlt: 'The Party Central explorer showing party game streaming data',
    },
    hero: {
        eyebrow: [text('Airhead Blog')],
        title: [text('Party Central Is Live')],
        description: [
            text('The Twitch party game data behind our monthly reports is now yours to explore.'),
        ],
        imageAsset: 'blogPartyCentral',
        imageAlt: 'The Party Central explorer showing party game streaming data',
        showDescription: true,
        background: 'blurred-image',
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-08-17T00:00:00.000Z'),
        tags: ['party-central', 'features'],
    },
    sections: [
        section({
            id: 'article',
            blocks: [
                p([
                    text('There is no good place to look up what party game streaming actually looks like. You can open Twitch and see who is live this minute, but you cannot ask which games the biggest streamers are playing, or what hour Spanish-language party streams peak, or whether a game is increasing or decreasing in popularity. I have been collecting the data to answer questions like that for a while now. The result is '),
                    link('/party-central', 'Party Central'),
                    text(', a data explorer for discovering and understanding what is happening with party game streaming on Twitch.'),
                ]),
            ],
            sections: [
                section({
                    id: 'article__origin-story',
                    title: 'Origin Story',
                    blocks: [
                        p([
                            text(`Data collection began as a way to satiate my own curiosity. I wanted to know which streamers might enjoy Airhead Games, but there's not enough time in the world for one person to watch all the party game streams on Twitch. Instead, I built a system that goes to Twitch every couple of hours to find out who is currently streaming and what party games they are playing.`),
                        ]),
                        p([
                            text('Around the new year it became obvious the rich data being collected could be worthwhile to others, such as game developers researching their audience, streamers looking for up-and-coming games to feature, viewers hunting for the best party game content, or industry analysts tracking genre trends. Published properly, it would be a genuine snapshot of the state of party gaming, and it fit with my ambition for Airhead Games to be known as the place for party games. The '),
                            link('/party-central/reports', 'monthly reports'),
                            text(' were started, with the idea that a data explorer would supplement them: reports broadly answering what is happening at a moment in time and the explorer offering the granular capabilities to dig into the data and answer specific questions.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__what-you-can-ask-it',
                    title: 'What You Can Ask It',
                    blocks: [
                        p([
                            text('Party Central is one screen. You pick a '),
                            em('Measure'),
                            text(', choose what to '),
                            em('Group by'),
                            text(', and optionally '),
                            em('Break down by'),
                            text(' a second thing. The data can be visualized as either a graphic or a table.'),
                        ]),
                        list({
                            items: [
                                [
                                    text('Filter to specific dates, games, streamers, or languages.'),
                                ],
                                [
                                    text('Click any graph or data cell to get the raw stream observations behind it.'),
                                ],
                                [
                                    text('Every view is a URL. The '),
                                    em('Copy view link'),
                                    text(' button allows you to hand someone the exact thing you are looking at.'),
                                ],
                                [
                                    text('Party Score is in there too, our proprietary composite ranking for games and streamers.'),
                                ],
                            ],
                        }),
                        p([
                            text(`Data collection began in February 2026, and the explorer will keep a rolling 24-month window as new data becomes available. Data refreshes about seven times a day; typically our Twitch researcher takes a look around every 3.5 hours. One caveat worth knowing before you draw conclusions: this is snapshot data rather than continuous measurement. Every stream observation represents the moment in time where a streamer was live. The language, game, and viewer count at that time are recorded.`),
                        ]),
                    ],
                }),
                section({
                    id: 'article__go-digging',
                    title: 'Go Digging',
                    blocks: [
                        p([
                            text(`Nobody asked for Party Central. Building an analytics tool is not typically what an indie game developer would spend their time on. Despite this, it's useful to me and it would make me happy if anyone else finds it helpful. If Party Central is challenging to navigate, I want your `),
                            link(buildSupportMailto('Party Central feedback'), 'feedback'),
                            text(` to make it better. The `),
                            link('/party-central/reports', 'monthly reports'),
                            text(` are still there, and can be a good starting point to learn about what's happening in the world of party games. For anything else you are curious about, open up `),
                            link('/party-central', 'Party Central'),
                            text(' and start exploring.'),
                        ]),
                    ],
                }),
            ],
        }),
        section({
            id: 'article-actions',
            widgets: [
                {
                    type: 'relatedDocuments',
                    limit: 4,
                },
            ],
        }),
    ],
});
//# sourceMappingURL=party-central-is-live.js.map